import { useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * A field's helper text behind an "i" icon, placed beside its label — so the label row stays one
 * line and the form isn't crowded with help text (the company account's labels, and the product's
 * EU device identifiers).
 *
 * The hint opens on hover with a mouse and on a tap on touch screens (where there's no hover); a
 * tap elsewhere closes it. A Popover rather than a Tooltip: Radix tooltips are hover-only and close
 * themselves on a tap.
 */
export function InfoHint({ hint }: { hint: string }) {
  const [open, setOpen] = useState(false);
  const pointer = useRef<string>('');
  const hover = (e: React.PointerEvent, next: boolean) => {
    if (e.pointerType === 'mouse') setOpen(next);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* Out of the Tab order: tabbing from the previous box goes straight to this field's
            input. -my-1 keeps the icon from making the label row taller than its neighbour's. */}
        <button
          type="button"
          tabIndex={-1}
          aria-label={hint}
          onPointerDown={(e) => (pointer.current = e.pointerType)}
          onPointerEnter={(e) => hover(e, true)}
          onPointerLeave={(e) => hover(e, false)}
          onClick={(e) => {
            // A mouse already opened it on hover — don't let the click toggle it shut.
            if (pointer.current === 'mouse') e.preventDefault();
          }}
          className="-my-1 shrink-0 text-brand-blue transition-colors hover:text-navy"
        >
          <Info className="size-4" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        // Never move focus on open OR close: closing returned focus to the icon, so the box being
        // typed in lost focus (its on-leave tidy-up could wipe a half-typed value) and the next
        // keystrokes went nowhere.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="w-auto max-w-xs border-line px-3 py-2 text-[11px] leading-relaxed text-ink"
      >
        {hint}
      </PopoverContent>
    </Popover>
  );
}
